import type { AbTest, CampaignTotals, TrackingStatus } from '@platform/contracts';
import {
  Badge,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  IconBadge,
  type IconBadgeProps,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  cn,
} from '@platform/ui';
import {
  FlaskConical,
  Handshake,
  Info,
  MailOpen,
  MousePointerClick,
  PlayCircle,
  Trophy,
} from 'lucide-react';
import type { ReactNode } from 'react';

import { fmtNum, fmtPct } from '../lib/console';
import { fmtAgo } from '../lib/format';

/** Lo que se le explica a quien ve "No se mide" (Instantly no registra nada sin seguimiento). */
export const NOT_MEASURED_HELP: Record<'opens' | 'clicks', string> = {
  opens:
    'El seguimiento de aperturas está desactivado en esta campaña para mejorar la entrega de los emails; medimos respuestas e interesados.',
  clicks:
    'El seguimiento de clics está desactivado en esta campaña para mejorar la entrega de los emails; medimos respuestas e interesados.',
};

const TRACKING_LABEL: Record<'opens' | 'clicks', string> = {
  opens: 'aperturas',
  clicks: 'clics',
};

/** Lo que se le explica a quien ve "Pendiente": el seguimiento está prendido, falta que salgan envíos. */
export function pendingTrackingHelp(kind: 'opens' | 'clicks', since: string | null): string {
  const label = TRACKING_LABEL[kind];
  if (!since)
    return `El seguimiento de ${label} está activo. En cuanto salgan los próximos envíos verás la tasa aquí.`;
  return `El seguimiento de ${label} está activo desde el ${fmtShortDate(since)}. En cuanto salgan los próximos envíos verás la tasa aquí.`;
}

const shortDate = new Intl.DateTimeFormat('es-ES', {
  day: 'numeric',
  month: 'short',
  timeZone: 'UTC',
});

/** `2026-09-24` → "24 sept". */
export function fmtShortDate(date: string): string {
  // Espacio duro: "19 sept" nunca se parte en dos líneas.
  return shortDate
    .format(new Date(`${date}T00:00:00Z`))
    .replace('.', '')
    .replace(' ', ' ');
}

export function fmtCurrency(value: number, currency: string): string {
  return new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency,
    useGrouping: 'always',
    maximumFractionDigits: 0,
  }).format(value);
}

/** "No se mide" con su explicación (tooltip accesible con el foco). */
export function NotMeasured({
  kind,
  className,
}: {
  kind: 'opens' | 'clicks';
  className?: string | undefined;
}) {
  return (
    <span className={cn('inline-flex items-center gap-1', className)}>
      No se mide
      {/* Provider propio: el componente también se usa fuera del árbol principal (tests, vistas). */}
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              className="inline-flex size-5 items-center justify-center rounded-full text-current opacity-70 outline-none hover:opacity-100 focus-visible:ring-2 focus-visible:ring-ring"
              aria-label={NOT_MEASURED_HELP[kind]}
            >
              <Info className="size-3.5" aria-hidden />
            </button>
          </TooltipTrigger>
          <TooltipContent className="max-w-72 leading-snug">
            {NOT_MEASURED_HELP[kind]}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </span>
  );
}

/** "Pendiente" con su explicación: el seguimiento está prendido pero todavía no salió nada. */
export function TrackingPending({
  kind,
  since,
  className,
}: {
  kind: 'opens' | 'clicks';
  since: string | null;
  className?: string | undefined;
}) {
  const help = pendingTrackingHelp(kind, since);
  return (
    <span className={cn('inline-flex items-center gap-1', className)}>
      Pendiente
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              className="inline-flex size-5 items-center justify-center rounded-full text-current opacity-70 outline-none hover:opacity-100 focus-visible:ring-2 focus-visible:ring-ring"
              aria-label={help}
            >
              <Info className="size-3.5" aria-hidden />
            </button>
          </TooltipTrigger>
          <TooltipContent className="max-w-72 leading-snug">{help}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </span>
  );
}

/**
 * Aclaración de una tasa medida sólo en parte: "desde 24 sept" (cuenta desde ese día) o "activo
 * desde 23 sept" (todavía sin envíos con seguimiento: esa es la fecha real de alta, no desde la
 * que cuenta la tasa, que no aplica todavía) o "solo campañas con seguimiento".
 */
export function trackingNote(t: TrackingStatus): string | null {
  if (t.reason === 'no_tracked_sends') {
    return t.since ? `activo desde ${fmtShortDate(t.since)}` : 'todavía sin envíos con seguimiento';
  }
  if (t.status !== 'partial') return null;
  return t.since ? `desde ${fmtShortDate(t.since)}` : 'solo campañas con seguimiento';
}

/** ¿Está realmente apagado el seguimiento? (compatible con respuestas viejas sin `reason`). */
function isTrackingOff(t: TrackingStatus): boolean {
  return t.reason === 'tracking_off' || (t.reason === undefined && t.status === 'none');
}

/** Tasa de apertura/clic respetando el seguimiento: "No se mide", "Pendiente" o el porcentaje + nota. */
export function TrackedRate({
  rate,
  tracking,
  kind,
  className,
  noteClassName,
}: {
  rate: number | null;
  tracking: TrackingStatus;
  kind: 'opens' | 'clicks';
  className?: string | undefined;
  noteClassName?: string | undefined;
}) {
  if (isTrackingOff(tracking)) return <NotMeasured kind={kind} className={className} />;
  if (tracking.reason === 'no_tracked_sends') {
    return <TrackingPending kind={kind} since={tracking.since} className={className} />;
  }
  const note = trackingNote(tracking);
  return (
    <span className="inline-flex flex-wrap items-baseline gap-x-1.5">
      <span>{fmtPct(rate)}</span>
      {note ? (
        <span className={cn('text-xs font-normal text-muted-foreground', noteClassName)}>
          ({note})
        </span>
      ) : null}
    </span>
  );
}

function Tile({
  label,
  icon,
  tone,
  value,
  sub,
  className,
}: {
  label: string;
  icon: ReactNode;
  tone: IconBadgeProps['tone'];
  value: ReactNode;
  sub?: ReactNode;
  className?: string | undefined;
}) {
  return (
    <div
      className={cn(
        'flex min-w-0 flex-col gap-2 rounded-xl border bg-card p-4 shadow-card',
        className,
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-medium text-muted-foreground">{label}</p>
        <IconBadge tone={tone} size="sm">
          {icon}
        </IconBadge>
      </div>
      <div className="font-display text-2xl leading-tight font-semibold tabular-nums">{value}</div>
      {sub ? <div className="text-xs text-muted-foreground">{sub}</div> : null}
    </div>
  );
}

/**
 * La cabecera del panel de Instantly: secuencias iniciadas, apertura, clics y oportunidades
 * (totales desde que empezó cada campaña) y «Pidieron información» (en el período elegido). Los clics desaparecen si nadie los mide.
 */
/** Valor de la tarjeta de tasa: "No se mide", "Pendiente" o el porcentaje. */
function rateTileValue(tracking: TrackingStatus, rate: number | null, kind: 'opens' | 'clicks') {
  if (isTrackingOff(tracking)) return <NotMeasured kind={kind} className="text-lg" />;
  if (tracking.reason === 'no_tracked_sends') {
    return <TrackingPending kind={kind} since={tracking.since} className="text-lg" />;
  }
  return fmtPct(rate);
}

/** Nota de la tarjeta de tasa: nada si está apagado, si no el conteo + la aclaración de fecha. */
function rateTileSub(tracking: TrackingStatus, countLabel: string) {
  if (isTrackingOff(tracking)) return null;
  if (tracking.reason === 'no_tracked_sends') return trackingNote(tracking);
  return [countLabel, trackingNote(tracking)].filter(Boolean).join(' · ');
}

export function InstantlyTotals({
  totals,
  compact = false,
}: {
  totals: CampaignTotals;
  compact?: boolean;
}) {
  const showClicks = !isTrackingOff(totals.clickTracking);
  return (
    <div
      className={cn(
        'grid gap-3',
        compact
          ? 'grid-cols-2 sm:grid-cols-3 xl:grid-cols-5'
          : 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-5',
      )}
    >
      <Tile
        label="Secuencias iniciadas"
        icon={<PlayCircle />}
        tone="navy"
        value={fmtNum(totals.sequencesStarted)}
        sub={`${fmtNum(totals.replies)} respuestas (${fmtPct(totals.replyRate)})`}
      />
      <Tile
        label="Tasa de apertura"
        icon={<MailOpen />}
        tone="cyan"
        value={rateTileValue(totals.openTracking, totals.openRate, 'opens')}
        sub={rateTileSub(
          totals.openTracking,
          `${fmtNum(totals.opens)} ${totals.opens === 1 ? 'persona' : 'personas'}`,
        )}
      />
      {showClicks ? (
        <Tile
          label="Tasa de clics"
          icon={<MousePointerClick />}
          tone="brand"
          value={rateTileValue(totals.clickTracking, totals.clickRate, 'clicks')}
          sub={rateTileSub(totals.clickTracking, `${fmtNum(totals.clicks)} clics`)}
        />
      ) : null}
      <Tile
        label="Oportunidades"
        icon={<Handshake />}
        tone="green"
        value={fmtNum(totals.opportunities)}
        sub={`Valor ${fmtCurrency(totals.opportunityValue, totals.currency)}`}
      />
      <Tile
        label="Pidieron información"
        icon={<Trophy />}
        tone="success"
        value={fmtNum(totals.infoRequested)}
        sub={
          <>
            Respondieron interesados o recibieron el dossier · en el período elegido
            {totals.conversions > 0 ? (
              <span className="mt-1 block">
                Marcados como ganado en Instantly: {fmtNum(totals.conversions)}
              </span>
            ) : null}
          </>
        }
        // Con 5 tarjetas en 2 columnas (móvil), la última ocupa la fila entera en vez de quedar huérfana.
        className={showClicks ? 'max-sm:col-span-2' : undefined}
      />
    </div>
  );
}

export function TotalsFootnote({ updatedAt }: { updatedAt: string | null }) {
  return (
    <p className="mt-2 text-xs text-muted-foreground">
      Totales desde que empezó cada campaña, con las mismas cuentas que el panel de Instantly
      {updatedAt ? ` · actualizado ${fmtAgo(updatedAt)}` : ''}. Los valores están en la moneda de
      Instantly.
    </p>
  );
}

/** Pruebas A/B: una tabla por email de la secuencia, una fila por versión. */
export function AbTests({ tests, showCampaign }: { tests: AbTest[]; showCampaign: boolean }) {
  if (tests.length === 0) return null;
  return (
    <div className="flex flex-col gap-4">
      {tests.map((t) => {
        const showClicks = !isTrackingOff(t.clickTracking);
        return (
          <Card key={`${t.campaignId}-${String(t.step)}`}>
            <CardHeader>
              <CardTitle icon={<FlaskConical />}>
                {showCampaign ? `${t.campaignName} · ` : ''}Email {t.step} de la secuencia
              </CardTitle>
              <CardDescription
                className={cn(
                  'flex items-start gap-2',
                  t.verdict.status === 'leader' && 'text-foreground',
                )}
              >
                {t.verdict.status === 'leader' ? (
                  <Trophy className="mt-0.5 size-4 shrink-0 text-success" aria-hidden />
                ) : null}
                {t.verdict.message}
              </CardDescription>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              <table className="w-full min-w-[44rem] text-sm">
                <thead>
                  <tr className="border-b text-left text-xs text-muted-foreground">
                    <th className="py-2 pr-3 font-medium">Versión</th>
                    <th className="py-2 pr-3 font-medium">Asunto y comienzo</th>
                    <th className="py-2 pr-3 text-right font-medium">Enviados</th>
                    <th className="py-2 pr-3 text-right font-medium">Apertura</th>
                    <th className="py-2 pr-3 text-right font-medium">Respuesta</th>
                    {showClicks ? (
                      <th className="py-2 pr-3 text-right font-medium">Clics</th>
                    ) : null}
                    <th className="py-2 text-right font-medium">Oportunidades</th>
                  </tr>
                </thead>
                <tbody>
                  {t.variants.map((v) => {
                    const lead = t.verdict.leader === v.label;
                    return (
                      <tr
                        key={v.label}
                        className={cn('border-b last:border-0', lead && 'bg-success/8')}
                      >
                        <td className="py-2.5 pr-3 align-top">
                          <span className="inline-flex items-center gap-1.5 font-display font-semibold">
                            {v.label}
                            {lead ? <Badge variant="success">Va ganando</Badge> : null}
                          </span>
                        </td>
                        <td className="max-w-md py-2.5 pr-3 align-top">
                          <p className="font-medium">{v.subject ?? '—'}</p>
                          {v.bodyPreview ? (
                            <p className="line-clamp-2 text-xs text-muted-foreground">
                              {v.bodyPreview}
                            </p>
                          ) : null}
                        </td>
                        <td className="py-2.5 pr-3 text-right align-top tabular-nums">
                          {fmtNum(v.sent)}
                        </td>
                        <td className="py-2.5 pr-3 text-right align-top tabular-nums">
                          <TrackedRate
                            rate={v.openRate}
                            tracking={t.openTracking}
                            kind="opens"
                            className="justify-end"
                          />
                        </td>
                        <td className="py-2.5 pr-3 text-right align-top tabular-nums">
                          {fmtPct(v.replyRate)}{' '}
                          <span className="text-xs text-muted-foreground">
                            ({fmtNum(v.replies)})
                          </span>
                        </td>
                        {showClicks ? (
                          <td className="py-2.5 pr-3 text-right align-top tabular-nums">
                            {v.clicks === null ? '—' : fmtNum(v.clicks)}
                          </td>
                        ) : null}
                        <td className="py-2.5 text-right align-top tabular-nums">
                          {fmtNum(v.opportunities)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
